import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Pause, Volume2, VolumeX, RotateCcw, Heart, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { CinematicVideoSemanticLayer } from './CinematicVideoSemanticLayer';

// Cloudinary adaptive delivery: f_auto picks the best codec per browser (av1/vp9/h264),
// q_auto compresses for the device, and vc_auto applies an efficient video codec.
// This dramatically reduces stalls on mobile + slow networks vs. the raw mp4.
const VIDEO_BASE = 'https://res.cloudinary.com/dr6xnqoxw/video/upload';
const VIDEO_ID = 'Screen_Recording_20260320_175858_Chrome_re28i6';
const VIDEO_SRC = `${VIDEO_BASE}/f_auto:video,q_auto,vc_auto/${VIDEO_ID}.mp4`;
const VIDEO_SRC_WEBM = `${VIDEO_BASE}/f_webm,q_auto,vc_auto/${VIDEO_ID}.webm`;
const VIDEO_SRC_MP4 = `${VIDEO_BASE}/f_mp4,q_auto,vc_h264/${VIDEO_ID}.mp4`;
const VIDEO_POSTER = `${VIDEO_BASE}/so_0,f_jpg,q_auto/${VIDEO_ID}.jpg`;

const copy = {
  en: {
    endHeadline: 'Someone in your life needs to hear from you.',
    endSub: 'Before another moment passes… say what matters.',
    start: 'Start Your Message',
    explore: 'Explore Say It NOW',
    close: 'Close video',
    replay: 'Replay',
    play: 'Play',
    pause: 'Pause',
    mute: 'Mute',
    unmute: 'Unmute',
  },
  es: {
    endHeadline: 'Alguien en tu vida necesita oírte.',
    endSub: 'Antes de que pase otro momento… di lo que importa.',
    start: 'Crea Tu Mensaje', explore: 'Explorar Say It NOW',
    close: 'Cerrar video', replay: 'Repetir', play: 'Reproducir', pause: 'Pausar', mute: 'Silenciar', unmute: 'Activar sonido',
  },
  pt: {
    endHeadline: 'Alguém na sua vida precisa ouvir você.',
    endSub: 'Antes que outro momento passe… diga o que importa.',
    start: 'Crie Sua Mensagem', explore: 'Explorar Say It NOW',
    close: 'Fechar vídeo', replay: 'Repetir', play: 'Reproduzir', pause: 'Pausar', mute: 'Silenciar', unmute: 'Ativar som',
  },
  fr: {
    endHeadline: 'Quelqu\'un dans votre vie a besoin de vous entendre.',
    endSub: 'Avant qu\'un autre instant ne passe… dites ce qui compte.',
    start: 'Créer Votre Message', explore: 'Découvrir Say It NOW',
    close: 'Fermer la vidéo', replay: 'Rejouer', play: 'Lire', pause: 'Pause', mute: 'Couper', unmute: 'Activer le son',
  },
  ar: {
    endHeadline: 'شخص في حياتك يحتاج أن يسمع منك.',
    endSub: 'قبل أن تمر لحظة أخرى… قل ما يهم.',
    start: 'أنشئ رسالتك', explore: 'استكشف Say It NOW',
    close: 'إغلاق الفيديو', replay: 'إعادة', play: 'تشغيل', pause: 'إيقاف', mute: 'كتم', unmute: 'إلغاء الكتم',
  },
  ja: {
    endHeadline: 'あなたの言葉を待っている人がいます。',
    endSub: 'また時が過ぎる前に、大切なことを伝えて。',
    start: 'メッセージを作る', explore: 'Say It NOW を見る',
    close: '閉じる', replay: 'もう一度', play: '再生', pause: '一時停止', mute: 'ミュート', unmute: 'ミュート解除',
  },
  ko: {
    endHeadline: '당신의 말을 기다리는 사람이 있습니다.',
    endSub: '한 순간이 더 지나기 전에… 마음을 전하세요.',
    start: '메시지 만들기', explore: 'Say It NOW 둘러보기',
    close: '닫기', replay: '다시 보기', play: '재생', pause: '일시정지', mute: '음소거', unmute: '음소거 해제',
  },
  id: {
    endHeadline: 'Seseorang dalam hidupmu perlu mendengar darimu.',
    endSub: 'Sebelum momen lain berlalu… katakan yang penting.',
    start: 'Buat Pesan Anda', explore: 'Jelajahi Say It NOW',
    close: 'Tutup', replay: 'Putar ulang', play: 'Putar', pause: 'Jeda', mute: 'Bisukan', unmute: 'Aktifkan suara',
  },
  hi: {
    endHeadline: 'आपके जीवन में किसी को आपकी ज़रूरत है।',
    endSub: 'एक और पल बीतने से पहले… जो ज़रूरी है वो कहें।',
    start: 'अपना संदेश बनाएँ', explore: 'Say It NOW देखें',
    close: 'बंद करें', replay: 'फिर से चलाएँ', play: 'चलाएँ', pause: 'रोकें', mute: 'मौन', unmute: 'ध्वनि चालू',
  },
};

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CinematicVideoModal({ open, onClose }: Props) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = lc(copy, language);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [ended, setEnded] = useState(false);

  // Lock body scroll + ESC to close
  useEffect(() => {
    if (!open) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = orig;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  // Autoplay on open, reset on close — wait until we have enough data to play through
  // smoothly so the video doesn't immediately stall after starting.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (open) {
      setEnded(false);
      setProgress(0);
      try { v.currentTime = 0; } catch {}
      v.muted = false;
      setIsMuted(false);
      // Force buffering to start immediately
      v.preload = 'auto';
      try { v.load(); } catch {}

      const tryPlay = () => {
        v.play().then(() => setIsPlaying(true)).catch(() => {
          v.muted = true;
          setIsMuted(true);
          v.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
        });
      };

      if (v.readyState >= 3) {
        tryPlay();
      } else {
        const onReady = () => { v.removeEventListener('canplay', onReady); tryPlay(); };
        v.addEventListener('canplay', onReady, { once: true });
        // Safety: if metadata is slow, still attempt after 1.5s
        const t = window.setTimeout(tryPlay, 1500);
        return () => { v.removeEventListener('canplay', onReady); window.clearTimeout(t); };
      }
    } else {
      v.pause();
      setIsPlaying(false);
    }
  }, [open]);

  const togglePlay = () => {
    const v = videoRef.current; if (!v) return;
    if (v.paused) { v.play(); setIsPlaying(true); } else { v.pause(); setIsPlaying(false); }
  };
  const toggleMute = () => {
    const v = videoRef.current; if (!v) return;
    v.muted = !v.muted; setIsMuted(v.muted);
  };
  const replay = () => {
    const v = videoRef.current; if (!v) return;
    v.currentTime = 0; setEnded(false); v.play(); setIsPlaying(true);
  };

  return (
    <>
      <CinematicVideoSemanticLayer videoUrl={VIDEO_SRC} />
      <AnimatePresence>
        {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-0 md:p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Say It NOW story"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Backdrop */}
          <motion.button
            type="button"
            aria-label={t.close}
            onClick={onClose}
            className="absolute inset-0 bg-black/[0.82] backdrop-blur-md cursor-default"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          />

          {/* Ambient emerald/violet glow */}
          <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
            <motion.div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[80vh] w-[80vw] max-w-[1200px] rounded-full blur-[120px]"
              style={{ background: 'radial-gradient(circle, hsl(var(--primary)/0.35), transparent 60%)' }}
              animate={{ scale: [1, 1.08, 1], opacity: [0.6, 0.9, 0.6] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[60vh] w-[60vw] max-w-[900px] rounded-full blur-[100px]"
              style={{ background: 'radial-gradient(circle, hsl(270 80% 60% / 0.25), transparent 65%)' }}
              animate={{ scale: [1.05, 1, 1.05], opacity: [0.5, 0.8, 0.5] }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            />
          </div>

          {/* Modal container */}
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 10 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 w-full h-full md:h-auto md:w-[75vw] md:max-w-[1100px] md:aspect-video md:rounded-[28px] overflow-hidden bg-black md:shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9),0_0_80px_-10px_hsl(var(--primary)/0.4)]"
          >
            {/* Video — multiple sources so the browser can pick the smallest format
                it natively supports (webm/vp9 for Chrome/Firefox, h264 mp4 fallback). */}
            <video
              ref={videoRef}
              poster={VIDEO_POSTER}
              playsInline
              preload="auto"
              crossOrigin="anonymous"
              className="absolute inset-0 w-full h-full object-cover"
              onTimeUpdate={(e) => {
                const v = e.currentTarget;
                if (v.duration) setProgress((v.currentTime / v.duration) * 100);
              }}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => { setEnded(true); setIsPlaying(false); }}
              onClick={togglePlay}
            >
              <source src={VIDEO_SRC_WEBM} type="video/webm" />
              <source src={VIDEO_SRC_MP4} type="video/mp4" />
              <source src={VIDEO_SRC} type="video/mp4" />
            </video>

            {/* Top gradient + close */}
            <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />
            <button
              onClick={onClose}
              aria-label={t.close}
              className="absolute top-4 right-4 z-20 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 backdrop-blur-md text-white flex items-center justify-center transition-colors ring-1 ring-white/20"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Bottom controls */}
            {!ended && (
              <div className="absolute inset-x-0 bottom-0 z-20 pt-16 pb-4 px-4 md:px-6 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                {/* Progress */}
                <div className="h-1 w-full rounded-full bg-white/20 overflow-hidden mb-3">
                  <motion.div
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${progress}%` }}
                    transition={{ ease: 'linear' }}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={togglePlay}
                    aria-label={isPlaying ? t.pause : t.play}
                    className="h-10 w-10 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md text-white flex items-center justify-center transition-colors"
                  >
                    {isPlaying ? <Pause className="h-4 w-4" fill="currentColor" /> : <Play className="h-4 w-4 ml-0.5" fill="currentColor" />}
                  </button>
                  <button
                    onClick={replay}
                    aria-label={t.replay}
                    className="h-10 w-10 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md text-white flex items-center justify-center transition-colors"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                  <button
                    onClick={toggleMute}
                    aria-label={isMuted ? t.unmute : t.mute}
                    className="h-10 w-10 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md text-white flex items-center justify-center transition-colors ml-auto"
                  >
                    {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            )}

            {/* Post-video emotional overlay */}
            <AnimatePresence>
              {ended && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.7 }}
                  className="absolute inset-0 z-30 flex flex-col items-center justify-center text-center px-6 bg-gradient-to-b from-black/70 via-black/85 to-black/95"
                >
                  {/* Soft particles */}
                  <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
                    {[...Array(14)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute h-1 w-1 rounded-full bg-primary/60"
                        style={{ left: `${(i * 17 + 5) % 100}%`, top: `${(i * 23 + 15) % 100}%` }}
                        animate={{ y: [0, -60, 0], opacity: [0, 0.9, 0] }}
                        transition={{ duration: 5 + (i % 4), repeat: Infinity, ease: 'easeInOut', delay: i * 0.4 }}
                      />
                    ))}
                    <motion.div
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[60%] w-[60%] rounded-full blur-3xl"
                      style={{ background: 'radial-gradient(circle, hsl(var(--primary)/0.3), transparent 70%)' }}
                      animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.9, 0.5] }}
                      transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                    />
                  </div>

                  <motion.h3
                    initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="relative font-heading font-extrabold text-white text-2xl sm:text-3xl md:text-5xl leading-tight max-w-2xl billboard-flash"
                  >
                    {t.endHeadline}
                  </motion.h3>
                  <motion.p
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.35 }}
                    className="relative mt-4 text-white/80 text-sm sm:text-base md:text-lg max-w-md"
                  >
                    {t.endSub}
                  </motion.p>

                  <motion.div
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.6 }}
                    className="relative mt-8 flex flex-col sm:flex-row items-center gap-3 w-full max-w-md"
                  >
                    <button
                      onClick={() => { onClose(); navigate('/try'); }}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-semibold shadow-2xl shadow-primary/40 hover:scale-[1.03] active:scale-[0.98] transition-transform"
                    >
                      <Heart className="h-4 w-4" fill="currentColor" />
                      {t.start}
                    </button>
                    <button
                      onClick={onClose}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white font-medium ring-1 ring-white/20 transition-colors"
                    >
                      {t.explore}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </motion.div>

                  <button
                    onClick={replay}
                    className="relative mt-6 inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    {t.replay}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
}

